import { useState, useCallback, useRef } from 'react';
import { useChatContext } from '../context/ChatContext';
import { ChatCommunicationService } from '../../../../services';

export const useChatCommunication = (agentId) => {
  const chatContext = useChatContext();
  const { communicationConfig } = chatContext;
  
  const [isConnected, setIsConnected] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  const communicationServiceRef = useRef(null);
  
  // Inicializar el servicio de comunicación
  if (!communicationServiceRef.current) {
    communicationServiceRef.current = new ChatCommunicationService(communicationConfig.type);
  }

  const checkConnection = useCallback(async () => {
    if (communicationConfig.type === 'http') {
      try {
        const isConnected = await communicationServiceRef.current.checkHttpConnection();
        setIsConnected(isConnected);
        setConnectionError(null);
        return isConnected;
      } catch (error) {
        setIsConnected(false);
        setConnectionError(error.message);
        return false;
      }
    } else {
      // Para GraphQL, asumimos que está conectado
      setIsConnected(true);
      setConnectionError(null);
      return true;
    }
  }, [communicationConfig.type]);

  const sendMessage = useCallback(async (params, options = {}) => {
    try {
      // Verificar conexión antes de enviar
      if (communicationConfig.type === 'http' && !isConnected) {
        const connected = await checkConnection();
        if (!connected) {
          throw new Error('No connection to HTTP API');
        }
      }

      const result = await communicationServiceRef.current.sendMessage(params, options);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send message');
      }

      return result;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  }, [communicationConfig.type, isConnected, checkConnection]);

  const subscribeToResponse = useCallback((responseId, options = {}) => {
    try {
      return communicationServiceRef.current.subscribeToResponse(responseId, options);
    } catch (error) {
      console.error('Failed to subscribe to response:', error);
      throw error;
    }
  }, []);

  const retryConnection = useCallback(async () => {
    return await checkConnection();
  }, [checkConnection]);

  return {
    isConnected,
    connectionError,
    sendMessage,
    subscribeToResponse,
    checkConnection,
    retryConnection,
    communicationType: communicationConfig.type
  };
};
